import { fetchPgArticleDetail, fetchTgArticleDetail } from '~/features/catalog/article.server';
import invariant from 'tiny-invariant';
import { AreaRedirectBehaviour, optionalProtectedLoader } from '~/shell/utils/authUtil.server';
import type { Route } from './+types/ArticleDetailRoute';
import BghArticleDetailView from '~/features/catalog/components/articleDetail/BghArticleDetailView';
import AghArticleDetailView from '~/features/catalog/components/articleDetail/AghArticleDetailView';
import type { BghArticleDetail } from '~/common/types/bghArticleDetail';
import { getSelectedLocation } from '~/shell/utils/locationUtil.server';
import { ArticleDetailBreadcrumb } from '~/features/catalog/components/articleDetail/ArticleDetailBreadcrumb';
import { RelatedProducts } from '~/features/catalog/components/article/RelatedProducts';
import type { AghArticleDetail } from '~/common/types/aghArticleDetail';
import { useSendDetailViewToGoogleAnalyticsEffect } from '~/tracking/googleAnalytics';
import { generateHreflangMeta, generateMeta } from '~/common/seo/metadata';
import { sdProduct } from '~/common/seo/structured-data/sdProduct';
import type { MetaDescriptor } from 'react-router';
import { useLocation, useParams } from 'react-router';
import { mergeRouteModuleMeta } from '~/shell/utils/metaUtil';
import { PgArea, TgArea } from '~/common/types/area';
import { PRICECODE_WEIGHTCONTROLLED } from '~/common/constants/ppConstants';
import { useTranslation } from 'react-i18next';
import { getQueryClient } from '~/queryClient.ts';
import { sdBrand } from '~/common/seo/structured-data/sdBrand.ts';
import { sdOffer } from '~/common/seo/structured-data/sdOffers.ts';
import { SERVER_CONFIG } from '~/config/config.server.ts';
import type { TFunction } from 'i18next';
import type { RouteTrackingData } from '~/tracking/trackingTypes.ts';
import { getProductImageUrl } from '../../../common/utils/imageUrlProvider/imageUrlProvider.server';
import { RecommendedProducts } from '~/features/catalog/components/article/RecommendedProducts.tsx';
import { isSettingActive } from '~/common/utils/featureUtil.server.ts';
import { SETTINGS } from '~/shell/types/settings.ts';

function getArticleSellUnitText<
  T extends {
    articleNumber: string;
    sellUnit: string;
    sellAmount: number;
    priceCode: number;
    approxWeight?: number;
    unitText: string;
  }
>(article: T, t: TFunction): string {
  const sellUnitPart = article.sellAmount > 1 ? article.sellUnit + ` ${t('sellUnitSeparator')} ` : '';
  const amountPart =
    article.priceCode === PRICECODE_WEIGHTCONTROLLED ? article.approxWeight?.toFixed(3) : article.sellAmount;

  return `${sellUnitPart}${amountPart} ${article.unitText}`;
}

export const loader = optionalProtectedLoader(
  async ({ params, request, url, loginInfo, t, area, api }) => {
    invariant(params.articleNo, 'Expected params.articleNo');

    const actionExclude = loginInfo?.actionExclude ?? false;
    const hasIndividualOrderEndTimes = loginInfo?.hasIndividualOrderEndTimes ?? false;

    const isTgArea = params.area === TgArea;

    const ccId = await getSelectedLocation(request);

    const articleDetail = isTgArea
      ? await fetchTgArticleDetail(api, params.articleNo, actionExclude, hasIndividualOrderEndTimes, t)
      : await fetchPgArticleDetail(api, params.articleNo, ccId, t);

    const isGkAirEnabled = await isSettingActive(request, SETTINGS.enableGkAir);

    const isAuthenticated = !!loginInfo;

    let meta: MetaDescriptor[] = [];

    // element not found
    if (!articleDetail) {
      return {
        isAuthenticated,
        isTgArea,
        meta
      };
    }

    const articleImageUrl = articleDetail?.celumId ? getProductImageUrl(articleDetail.celumId) : undefined;
    const sellUnitText = getArticleSellUnitText(articleDetail, t);

    const articleDescription =
      articleDetail.descriptionLong ??
      t(
        'Unser Sortiment bietet zartes Fleisch, frisches Gemüse sowie erfrischende Getränke und Bedarfsartikel – ideal für Gastronomie, Handel und anspruchsvolle Küchen.'
      );

    const customMetas: MetaDescriptor[] = [
      { property: 'og:type', content: 'product' },
      { property: 'product:retailer_item_id', content: articleDetail.articleNumber },
      { property: 'product:brand', content: articleDetail.brand },
      {
        'script:ld+json': sdProduct({
          '@type': 'Product',
          name: articleDetail.description,
          image: articleImageUrl,
          description: articleDescription,
          brand: sdBrand({
            '@type': 'Brand',
            name: articleDetail.brand
          }),
          sku: articleDetail.articleNumber,
          offers: sdOffer({
            '@type': 'Offer',
            priceCurrency: 'CHF',
            url: `${SERVER_CONFIG.FRONTEND_URL}${url.pathname}`,
            availability: 'https://schema.org/InStock',
            itemCondition: 'https://schema.org/NewCondition',
            seller: {
              '@type': 'Organization',
              name: 'Prodega'
            }
          })
        })
      }
    ];

    if ('PROD' === process.env.DEPLOYMENT_ENVIRONMENT && area === PgArea) {
      customMetas.push({ name: 'robots', content: 'all' });
    } else {
      customMetas.push({ name: 'robots', content: 'noindex, nofollow, noarchive' });
    }

    const articlePath = `${area}/catalog/article/${params.articleNo}`;
    const canonicalUrl = `${SERVER_CONFIG.FRONTEND_URL}/${params.lang}/${articlePath}`;
    const hreflangMeta = generateHreflangMeta(SERVER_CONFIG.FRONTEND_URL, articlePath);

    meta = generateMeta(
      {
        title:
          area === PgArea
            ? `${articleDetail.description}, ${sellUnitText} - ${t('bei Prodega kaufen')}`
            : `${articleDetail.description}, ${sellUnitText}`,
        description: articleDescription,
        url: url.href,
        image: articleImageUrl,
        canonical: canonicalUrl
      },
      [...customMetas, ...hreflangMeta]
    );

    return {
      articleDetail,
      isAuthenticated,
      isTgArea,
      meta,
      isGkAirEnabled
    };
  },
  { areaRedirectBehavior: AreaRedirectBehaviour.Auto }
);

export async function clientLoader({ request, serverLoader }: Route.ClientLoaderArgs) {
  const url = new URL(request.url);
  const cacheKey = ['article-detail', url.pathname];

  const queryClient = getQueryClient();
  let searchResult = queryClient.getQueryData(cacheKey) as Awaited<ReturnType<typeof loader>>;

  if (searchResult) {
    return searchResult;
  }

  searchResult = await serverLoader();

  queryClient.setQueryData(cacheKey, searchResult, {
    updatedAt: Date.now()
  });

  queryClient.setQueryDefaults(cacheKey, {
    staleTime: 1000 * 60 * 5
  });

  return searchResult;
}

export const meta: Route.MetaFunction = mergeRouteModuleMeta(({ loaderData }) => {
  return loaderData?.meta ?? [];
});

export const handle = {
  getRouteTrackingData(loaderData: Awaited<ReturnType<typeof loader>>): RouteTrackingData {
    let title = '';
    if (loaderData?.meta) {
      const titleMeta = loaderData.meta.find((m: MetaDescriptor) => 'title' in m);
      title = titleMeta?.['title'] ?? '';
    }

    return {
      pageName: title
    };
  }
};

/**
 * Route component for the article detail page.
 * Info: The loading indicator is handled on the AreaLayoutRoute.
 */
export default function ArticleDetailRoute({ loaderData }: Readonly<Route.ComponentProps>) {
  const { articleDetail, isAuthenticated, isTgArea, isGkAirEnabled } = loaderData;
  const { t } = useTranslation();
  const { articleNo } = useParams();

  if (!articleDetail) {
    return (
      <div className="grid place-items-center mt-8">
        <div>
          {' '}
          {t('Der Artikel mit der Artikelnummer "{{articleNumber}}" ist leider an Ihrem Standort nicht verfügbar.', {
            articleNumber: articleNo
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_260px] gap-4">
      <div className="overflow-x-auto">
        <ArticleDetail
          article={articleDetail}
          isBgh={isTgArea}
          isAuthenticated={isAuthenticated}
          key={articleNo}
          isGkAirEnabled={isGkAirEnabled}
        />
      </div>
      <div id="rightInfo" className="h-full">
        {/* Rechte Sidebar */}
      </div>
    </div>
  );
}

export function ArticleDetail({
  article,
  isBgh,
  isAuthenticated,
  isGkAirEnabled
}: Readonly<{
  article: AghArticleDetail | BghArticleDetail;
  isBgh: boolean;
  isAuthenticated: boolean;
  isGkAirEnabled: boolean;
}>) {
  useSendDetailViewToGoogleAnalyticsEffect(article);
  const location = useLocation();
  const state = location.state as { quantity?: number };

  return (
    <>
      <ArticleDetailBreadcrumb article={article} className={'mb-8 pl-4 desktop:pl-0 pt-4 desktop:pt-0'} />

      {isBgh ? (
        <BghArticleDetailView
          article={article as BghArticleDetail}
          isAuthenticated={isAuthenticated}
          initialQuantity={state?.quantity}
        />
      ) : (
        <AghArticleDetailView
          article={article as AghArticleDetail}
          isAuthenticated={isAuthenticated}
          initialQuantity={state?.quantity}
        />
      )}

      <RelatedProducts
        className={'my-16 pl-4 desktop:pl-0'}
        articleNumber={article.articleNumber}
        agrId={article.agrId}
      />

      {isGkAirEnabled && (
        <RecommendedProducts
          className={'my-16 pl-4 desktop:pl-0'}
          articleNumber={article.articleNumber}
          hwgId={article.hwgId}
          hagrCode={article.hagrCode}
          agrCode={article.agrCode}
        />
      )}
    </>
  );
}
